import { useState } from "react";

import {
  FormCategory,
  isRaffleEnabled,
  OrganizationRoutes,
  RouteBuilders,
  SignupFormEditorOpenedFrom,
  UserGeneralPermissions,
} from "@simplyk/common";
import { useRouter } from "next/navigation";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { FormCreationSource, useCreateForm } from "@/common/hooks/useCreateForm";
import { useInitialStatus } from "@/common/hooks/useInitialStatus";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { useRaffleComplianceGate } from "@/components/RaffleComplianceDisclaimer/helpers/useRaffleComplianceGate";
import { isSweepstakesCountry } from "@/features/LiveFormEditor/common/sweepstakes";
import { LOTTERY_TYPE_SEARCH_PARAM } from "@/features/LiveFormEditor/Selector/helpers/lotteryTypeSearchParam";
import type { ServiceEntryCategory } from "@/features/LiveFormEditor/Selector/ServiceEntryB/helpers/getServiceEntrySections";

export const useCreateCampaignType = ({ source }: { source: FormCreationSource }) => {
  const { organization, currentUser, isOrganizationOwner } = useCurrentUserContext();
  const { isoLocale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();
  const { logAmplitudeEvent } = useAmplitude();
  const buildUrl = useUrlBuilder();
  const router = useRouter();
  const { hasDonationForm, hasTicketing } = useInitialStatus();
  const isOnboarding = hasDonationForm === false && hasTicketing === false;
  const { createFormAndRedirect } = useCreateForm({ isOnboarding, source });
  const { mutateAsync: updateOnboardingServiceEntryFormCategory } =
    trpc.updateOnboardingServiceEntryFormCategory.useMutation();
  const { gateRaffleCreation, raffleComplianceDisclaimerDialog } = useRaffleComplianceGate();
  const [creatingCategory, setCreatingCategory] = useState<ServiceEntryCategory | null>(null);

  const isOrganizationSweepstakesCountry = isSweepstakesCountry(organization?.country);
  const canManageContacts =
    isOrganizationOwner ||
    Boolean(currentUser?.newPermissions?.generalPermissions?.includes(UserGeneralPermissions.Contacts));

  const isCreatable = (category: ServiceEntryCategory) => {
    if (category === "signUpForms") {
      return canManageContacts && !isSmallScreen;
    }
    return category !== FormCategory.Lottery || !organization?.country || isRaffleEnabled(organization.country);
  };

  const create = async (category: ServiceEntryCategory) => {
    setCreatingCategory(category);
    try {
      if (category === "signUpForms") {
        logAmplitudeEvent(AmplitudeEvents.SignupFormStarted, {
          openedFrom: SignupFormEditorOpenedFrom.OrganizationAssistant,
        });
        router.push(OrganizationRoutes.NewsletterFormsNew);
        return;
      }
      if (isOnboarding) {
        await updateOnboardingServiceEntryFormCategory({ formCategory: category });
      }
      await createFormAndRedirect(category);
    } finally {
      setCreatingCategory(null);
    }
  };

  const createCampaignType = async (category: ServiceEntryCategory) => {
    if (creatingCategory) {
      return;
    }
    if (category === FormCategory.Lottery && isOrganizationSweepstakesCountry) {
      router.push(
        buildUrl({
          route: RouteBuilders.buildCreateNewFormLink({ isoLocale }).addSearchParams({
            [LOTTERY_TYPE_SEARCH_PARAM]: "true",
          }),
        })
      );
      return;
    }
    if (category === FormCategory.Lottery && gateRaffleCreation(() => create(category))) {
      return;
    }
    await create(category);
  };

  return {
    createCampaignType,
    creatingCategory,
    isCreatable,
    isSweepstakesCountry: isOrganizationSweepstakesCountry,
    raffleComplianceDisclaimerDialog,
  };
};
